import React, { useEffect, useMemo, useState } from "react";
import type { CampaignSummary, ScheduledEmail } from "../types";
import { SectionCard } from "./SectionLayout";

interface ScheduledEmailsSectionProps {
  emails: ScheduledEmail[];
  isLoading: boolean;
  statusFilter: string;
  jobTypeFilter: string;
  campaignFilter: string;
  campaignOptions: CampaignSummary[];
  onChangeStatus: (value: string) => void;
  onChangeJobType: (value: string) => void;
  onChangeCampaign: (value: string) => void;
  onRefresh: () => void;
  onCancel: (email: ScheduledEmail) => Promise<void> | void;
}

const STATUS_OPTIONS = ["pending", "cancelled", "sent", "failed"] as const;

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  cancelled: "Cancelled",
  sent: "Sent",
  failed: "Failed",
};

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-50 text-amber-600",
  cancelled: "bg-gray-100 text-gray-600",
  sent: "bg-emerald-50 text-emerald-600",
  failed: "bg-red-50 text-red-600",
};

const JOB_TYPE_OPTIONS = ["sequence", "reachout", "no-chat-intro", "email-reply"] as const;

const JOB_TYPE_LABELS: Record<string, string> = {
  sequence: "Sequence",
  reachout: "Reach-out",
  "no-chat-intro": "No-chat intro",
  "email-reply": "Email Reply",
};

const formatDate = (value?: string) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const toTitle = (value: string) =>
  value
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const ScheduledQueueSection: React.FC<ScheduledEmailsSectionProps> = ({
  emails,
  isLoading,
  statusFilter,
  jobTypeFilter,
  campaignFilter,
  campaignOptions,
  onChangeStatus,
  onChangeJobType,
  onChangeCampaign,
  onRefresh,
  onCancel,
}) => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<
    | "user"
    | "jobType"
    | "campaign"
    | "subject"
    | "scheduledFor"
    | "sequence"
    | "status"
    | "createdAt"
  >("scheduledFor");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const statuses = useMemo(() => {
    const unique = new Set<string>(STATUS_OPTIONS);
    emails.forEach((email) => {
      if (email.status) {
        unique.add(email.status);
      }
    });
    return Array.from(unique);
  }, [emails]);

  const jobTypes = useMemo(() => {
    const unique = new Set<string>(JOB_TYPE_OPTIONS);
    emails.forEach((email) => {
      if (email.jobType) {
        unique.add(email.jobType);
      }
    });
    return Array.from(unique);
  }, [emails]);

  const filteredEmails = useMemo(() => {
    const normalizedQuery = search.trim().toLowerCase();
    const fromTime = fromDate ? new Date(fromDate).getTime() : null;
    const toTime = toDate
      ? new Date(toDate).getTime() + 24 * 60 * 60 * 1000 - 1
      : null;

    const filtered = emails.filter((email) => {
      const matchesStatus =
        statusFilter === "all" ? true : email.status === statusFilter;
      const matchesJobType =
        jobTypeFilter === "all" ? true : email.jobType === jobTypeFilter;
      const matchesCampaign =
        campaignFilter === "all" ? true : email.campaignId === campaignFilter;

      const matchesSearch =
        !normalizedQuery ||
        [email.email, email.emailSubject, email.promptTitle, email.userId]
          .filter((value): value is string => typeof value === "string")
          .some((value) => value.toLowerCase().includes(normalizedQuery));

      const scheduledTime = email.scheduledFor
        ? new Date(email.scheduledFor).getTime()
        : NaN;
      const matchesDate =
        (fromTime === null || scheduledTime >= fromTime) &&
        (toTime === null || scheduledTime <= toTime);

      return (
        matchesStatus &&
        matchesJobType &&
        matchesCampaign &&
        matchesSearch &&
        matchesDate
      );
    });

    const toSequenceNumber = (email: ScheduledEmail) =>
      typeof email.sequenceIndex === "number"
        ? email.sequenceIndex
        : typeof email.sequenceStepIndex === "number"
        ? email.sequenceStepIndex
        : Number.POSITIVE_INFINITY;

    const sorted = [...filtered].sort((a, b) => {
      let first: string | number = 0;
      let second: string | number = 0;

      switch (sortKey) {
        case "user":
          first = (a.email ?? "").toLowerCase();
          second = (b.email ?? "").toLowerCase();
          break;
        case "jobType":
          first = (a.jobType ?? "").toLowerCase();
          second = (b.jobType ?? "").toLowerCase();
          break;
        case "campaign":
          first = (a.campaignName ?? "").toLowerCase();
          second = (b.campaignName ?? "").toLowerCase();
          break;
        case "subject":
          first = (
            a.emailSubject ??
            a.promptTitle ??
            ""
          ).toLowerCase();
          second = (
            b.emailSubject ??
            b.promptTitle ??
            ""
          ).toLowerCase();
          break;
        case "scheduledFor":
          {
            const fallback =
              sortDirection === "asc"
                ? Number.POSITIVE_INFINITY
                : Number.NEGATIVE_INFINITY;
            first = a.scheduledFor
              ? new Date(a.scheduledFor).getTime()
              : fallback;
            second = b.scheduledFor
              ? new Date(b.scheduledFor).getTime()
              : fallback;
          }
          break;
        case "createdAt":
          {
            const fallback =
              sortDirection === "asc"
                ? Number.POSITIVE_INFINITY
                : Number.NEGATIVE_INFINITY;
            first = a.createdAt
              ? new Date(a.createdAt).getTime()
              : a.scheduledFor
              ? new Date(a.scheduledFor).getTime()
              : fallback;
            second = b.createdAt
              ? new Date(b.createdAt).getTime()
              : b.scheduledFor
              ? new Date(b.scheduledFor).getTime()
              : fallback;
          }
          break;
        case "sequence":
          first = toSequenceNumber(a);
          second = toSequenceNumber(b);
          break;
        case "status":
          first = (a.status ?? "").toLowerCase();
          second = (b.status ?? "").toLowerCase();
          break;
        default:
          break;
      }

      if (first < second) return sortDirection === "asc" ? -1 : 1;
      if (first > second) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [
    emails,
    statusFilter,
    jobTypeFilter,
    campaignFilter,
    search,
    fromDate,
    toDate,
    sortKey,
    sortDirection,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredEmails.length / Math.max(1, pageSize))
  );

  useEffect(() => {
    setPage(1);
  }, [search, fromDate, toDate, statusFilter, jobTypeFilter, campaignFilter, pageSize]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const startIndex = (page - 1) * pageSize;
  const visibleEmails = filteredEmails.slice(startIndex, startIndex + pageSize);

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  const sortIndicator = (key: typeof sortKey) => {
    if (sortKey !== key) return null;
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const handleCancel = async (email: ScheduledEmail) => {
    if (email.status !== "pending") {
      return;
    }
    const subject = email.emailSubject ?? email.promptTitle ?? "this email";
    const scheduledLabel = formatDate(email.scheduledFor);
    const confirmed = window.confirm(
      `Cancel ${subject} scheduled for ${scheduledLabel}?`
    );
    if (!confirmed) {
      return;
    }
    setCancellingId(email.id);
    try {
      await onCancel(email);
    } catch {
      // Parent handles error presentation
    } finally {
      setCancellingId(null);
    }
  };

  const renderStatus = (status?: string) => {
    if (!status) return null;
    const label = STATUS_LABELS[status] ?? toTitle(status);
    const chipClass = STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600";
    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${chipClass}`}
      >
        {label}
      </span>
    );
  };

  const renderJobType = (jobType?: string) => {
    if (!jobType) return "Sequence";
    return JOB_TYPE_LABELS[jobType] ?? toTitle(jobType);
  };

  const emptyStateLabel = isLoading
    ? "Loading scheduled emails..."
    : "No emails match the current filters.";

  return (
    <SectionCard
      title="Scheduled Emails"
      description="Review queued sends and cancel pending jobs before they fire."
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex-1 space-y-3">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by email, subject, or user ID"
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
          />
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
            <label className="flex flex-col gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
                Status
              </span>
              <select
                value={statusFilter}
                onChange={(event) => onChangeStatus(event.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                <option value="all">All statuses</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status] ?? toTitle(status)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
                Job type
              </span>
              <select
                value={jobTypeFilter}
                onChange={(event) => onChangeJobType(event.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                <option value="all">All job types</option>
                {jobTypes.map((jobType) => (
                  <option key={jobType} value={jobType}>
                    {renderJobType(jobType)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
                Campaign
              </span>
              <select
                value={campaignFilter}
                onChange={(event) => onChangeCampaign(event.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                <option value="all">All campaigns</option>
                {campaignOptions.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                    {campaign.status === "active" ? " (Live)" : ""}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-col gap-2 md:col-span-2 lg:col-span-1">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-600">
                Scheduled window
              </div>
              <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                  className="w-full min-w-[0] rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] md:flex-1"
                  placeholder="From"
                />
                <span className="text-xs font-semibold text-gray-500">to</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                  className="w-full min-w-[0] rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] md:flex-1"
                  placeholder="To"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="rounded-2xl bg-gray-50 px-4 py-3 text-sm text-gray-600">
            <div className="font-semibold text-gray-900">
              {filteredEmails.length} of {emails.length} jobs
            </div>
            <div className="text-xs text-gray-500">
              Pending jobs can be cancelled before execution.
            </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
            className="rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
          >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            className="inline-flex items-center justify-center rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-widest text-gray-500">
            <tr className="text-left">
              {[
                { label: "User", key: "user" as const },
                { label: "Job type", key: "jobType" as const },
                { label: "Campaign", key: "campaign" as const },
                { label: "Subject", key: "subject" as const },
                { label: "Scheduled for", key: "scheduledFor" as const },
                { label: "Sequence step", key: "sequence" as const },
                { label: "Status", key: "status" as const },
              ].map((column) => (
                <th key={column.key} className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => toggleSort(column.key)}
                    className="flex items-center gap-1 text-left uppercase tracking-widest"
                  >
                    <span>{column.label}</span>
                    <span className="text-[11px]">{sortIndicator(column.key)}</span>
                  </button>
                </th>
              ))}
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white text-sm text-gray-600">
            {isLoading && emails.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-sm text-gray-500"
                >
                  Loading scheduled emails...
                </td>
              </tr>
            ) : null}
            {!isLoading && filteredEmails.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-sm text-gray-500"
                >
                  {emptyStateLabel}
                </td>
              </tr>
            ) : null}
            {visibleEmails.map((item) => {
              const isPending = item.status === "pending";
              const subject =
                item.emailSubject ?? item.promptTitle ?? "Untitled email";
              const sequenceLabel =
                typeof item.sequenceIndex === "number" ||
                typeof item.sequenceStepIndex === "number"
                  ? `Step ${
                      (item.sequenceIndex ?? item.sequenceStepIndex ?? 0) + 1
                    }`
                  : "—";
              const userMessagesUrl = item.userId
                ? `/admin/messages/${item.userId}`
                : undefined;
              return (
                <tr key={item.id}>
                  <td className="px-4 py-3">
                    {userMessagesUrl ? (
                      <a
                        href={userMessagesUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex flex-col text-[var(--primary-color)]"
                      >
                        <span className="font-semibold group-hover:underline">
                          {item.email ?? "Unknown user"}
                        </span>
                        <span className="text-xs text-[var(--primary-color)]/80 group-hover:underline">
                          {item.userTimezone ??
                            item.timezone ??
                            "Unknown timezone"}
                        </span>
                      </a>
                    ) : (
                      <>
                        <div className="font-semibold text-gray-900">
                          {item.email ?? "Unknown user"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.userTimezone ??
                            item.timezone ??
                            "Unknown timezone"}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3">{renderJobType(item.jobType)}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">
                      {item.campaignName ?? "Unknown campaign"}
                    </div>
                  </td>
                  <td className="px-4 py-3">{subject}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">
                      {formatDate(item.scheduledFor)}
                    </div>
                    {item.scheduledForLocal ? (
                      <div className="text-xs text-gray-500">
                        Local: {formatDate(item.scheduledForLocal)}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">
                      {sequenceLabel}
                    </div>
                    {item.sequenceStepName ? (
                      <div className="text-xs text-gray-500">
                        {item.sequenceStepName}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">{renderStatus(item.status)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => handleCancel(item)}
                      disabled={!isPending || cancellingId === item.id}
                      className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-red-300 hover:text-red-500 disabled:cursor-not-allowed disabled:border-gray-100 disabled:text-gray-400"
                    >
                      {cancellingId === item.id ? "Cancelling..." : "Cancel"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-col items-start justify-between gap-3 text-sm text-gray-600 md:flex-row md:items-center">
        <div>
          Showing {visibleEmails.length ? startIndex + 1 : 0}-
          {Math.min(filteredEmails.length, startIndex + pageSize)} of{" "}
          {filteredEmails.length} jobs
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page === 1}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
          >
            Previous
          </button>
          <div className="text-xs font-semibold text-gray-700">
            Page {page} of {totalPages}
          </div>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page >= totalPages}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
          >
            Next
          </button>
        </div>
      </div>
    </SectionCard>
  );
};

export const ScheduledEmailsSection = ScheduledQueueSection;
